//@flow
import React, {useState} from 'react';
import {makeStyles, Typography, Chip, IconButton} from '@material-ui/core';
import {GetApp, DeleteOutline, EditOutlined} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import type {ChatTrigger} from '../../../../entities';
import FileTypeIcons from '../../../../elements/FileTypeIcons';
import {TiendadaSwitch} from '../../../../elements/tiendada-switch';
import {getTriggerAttachmentUrl, getTriggerTypeLabel} from '../utils';
import {
  getTextSearchFromTrigger,
  getInactivityFromTrigger,
  parseActionContentFromApi,
  getActionTypeFromApi,
  resolveInitMessageConfig,
} from '../utils/adapters';
import {
  getHourOptionLabelFromUtc,
  DEFAULT_EXECUTION_HOUR_SLOT,
  getStandardHourOptionLabel,
} from '../../constants/executionTime';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import TriggerSimulateEstimate from './TriggerSimulateEstimate';
import {isCustomerInactivityTrigger} from '../utils/triggerConfig';

type Props = {
  trigger: ChatTrigger,
  onEdit: () => void,
  onToggleStatus: (state: boolean) => void,
  onDelete: () => void,
};

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    border: `1px solid ${colors.grey300}`,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    height: '100%',
    transition: 'all 0.3s ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  chipsContainer: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  chip: {
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
  },
  activeChip: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
  },
  inactiveChip: {
    backgroundColor: '#F5F5F5',
    color: '#757575',
  },
  idChip: {
    backgroundColor: '#F3E5F5',
    color: '#7B1FA2',
  },
  typeChip: {
    backgroundColor: '#E3F2FD',
    color: '#1565C0',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: 'inherit',
    lineHeight: 1.4,
  },
  description: {
    fontSize: '0.9375rem',
    color: 'inherit',
    lineHeight: 1.5,
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  attachmentsLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1A1C29',
    marginBottom: '0.5rem',
  },
  attachmentsContainer: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    marginTop: 'auto',
  },
  attachmentItem: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    border: `1px solid ${colors.grey200}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    fontSize: '1.5rem',
    backgroundColor: '#FAFAFA',
    '&:hover $downloadOverlay': {
      opacity: 1,
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  downloadOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s',
    color: '#FFF',
  },
  sectionTitle: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#9E9E9E',
    marginBottom: '0.25rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  clickableSection: {
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    '&:hover': {
      color: colors.primary,
      opacity: 0.9,
    },
  },
  relatedTextsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  relatedTextChip: {
    backgroundColor: colors.grey100,
    color: colors.grey700,
    fontSize: '0.75rem',
    height: '24px',
  },
  deleteButton: {
    color: colors.error,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.04)',
    },
  },
  metaText: {
    fontSize: '0.875rem',
    color: colors.grey700,
    marginTop: '0.25rem',
  },
  estimateSection: {
    marginTop: '0.25rem',
  },
}));

function getActionPreview(trigger: ChatTrigger) {
  const action = trigger.actions[0];
  if (!action) return null;

  const actionType = getActionTypeFromApi(action);

  if (actionType === 'SEND_INIT_MESSAGE') {
    const initConfig = resolveInitMessageConfig(action);
    if (!initConfig.provider) {
      return {
        title: 'Iniciar conversación',
        text: '',
        images: [],
        videos: [],
        documents: [],
      };
    }

    if (initConfig.provider === 'WHATSAPP_API') {
      return {
        title: 'Iniciar conversación · WhatsApp API',
        text: `Template: ${initConfig.whatsappApiMessage?.template || '—'}`,
        images: [],
        videos: [],
        documents: [],
      };
    }

    const webMessage = parseActionContentFromApi(action);
    return {
      title: 'Iniciar conversación · WhatsApp Web',
      text: webMessage.text,
      images: webMessage.images || [],
      videos: webMessage.videos || [],
      documents: webMessage.documents || [],
    };
  }

  const message = parseActionContentFromApi(action);
  return {
    title: 'Respuesta',
    text: message.text,
    images: message.images || [],
    videos: message.videos || [],
    documents: message.documents || [],
  };
}

export default function TriggerCard({
  trigger,
  onEdit,
  onToggleStatus,
  onDelete,
}: Props) {
  const classes = useStyles();
  const [modalConfirmOpen, setModalConfirmOpen] = useState(false);

  const textSearch = getTextSearchFromTrigger(trigger);
  const inactivity = getInactivityFromTrigger(trigger);
  const actionPreview = getActionPreview(trigger);

  const openModalConfirm = e => {
    if (e) e.stopPropagation();
    setModalConfirmOpen(true);
  };
  const closeModalConfirm = () => setModalConfirmOpen(false);

  const handleToggle = e => {
    if (e) e.stopPropagation();
    onToggleStatus(!trigger.enabled);
  };

  return (
    <>
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={classes.chipsContainer}>
            <Chip
              label={trigger.enabled ? 'Activo' : 'Inactivo'}
              className={`${classes.chip} ${
                trigger.enabled ? classes.activeChip : classes.inactiveChip
              }`}
            />
            <Chip
              label={getTriggerTypeLabel(trigger.triggerType)}
              className={`${classes.chip} ${classes.typeChip}`}
            />
            {trigger.name && (
              <Chip
                label={trigger.name}
                className={`${classes.chip} ${classes.idChip}`}
              />
            )}
          </div>
          <div className={classes.headerActions}>
            <div onClick={e => e.stopPropagation()}>
              <TiendadaSwitch
                checked={trigger.enabled}
                onChange={handleToggle}
                size="small"
              />
            </div>
            <IconButton
              size="small"
              onClick={openModalConfirm}
              className={classes.deleteButton}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={classes.clickableSection} onClick={onEdit}>
          <Typography className={classes.sectionTitle}>
            Disparador
            <EditOutlined style={{fontSize: 16}} />
          </Typography>

          {textSearch && (
            <>
              <Typography className={classes.title}>
                {textSearch.searchText}
              </Typography>
              {textSearch.relatedSearchTexts &&
                textSearch.relatedSearchTexts.length > 0 && (
                  <div className={classes.relatedTextsContainer}>
                    {textSearch.relatedSearchTexts.map((item, index) => (
                      <Chip
                        key={index}
                        label={item}
                        className={classes.relatedTextChip}
                        size="small"
                      />
                    ))}
                  </div>
                )}
            </>
          )}

          {inactivity && (
            <Typography className={classes.metaText}>
              {inactivity.inactiveDays} días sin respuesta ·{' '}
              {trigger.executionTime
                ? getHourOptionLabelFromUtc(trigger.executionTime)
                : getStandardHourOptionLabel(DEFAULT_EXECUTION_HOUR_SLOT)}
            </Typography>
          )}

          {isCustomerInactivityTrigger(trigger.triggerType) && (
            <div className={classes.estimateSection}>
              <TriggerSimulateEstimate triggerId={trigger.id} enabled compact />
            </div>
          )}
        </div>

        {actionPreview && (
          <div
            style={{flexGrow: 1}}
            className={classes.clickableSection}
            onClick={onEdit}
          >
            <Typography className={classes.sectionTitle}>
              {actionPreview.title}
            </Typography>
            <Typography className={classes.description}>
              {actionPreview.text}
            </Typography>
          </div>
        )}

        {actionPreview &&
          ((actionPreview.images && actionPreview.images.length > 0) ||
            (actionPreview.videos && actionPreview.videos.length > 0) ||
            (actionPreview.documents &&
              actionPreview.documents.length > 0)) && (
            <div>
              <Typography className={classes.attachmentsLabel}>
                Adjuntos
              </Typography>
              <div className={classes.attachmentsContainer}>
                {actionPreview.documents?.map((doc, index) => (
                  <a
                    key={`doc-${index}`}
                    className={classes.attachmentItem}
                    href={getTriggerAttachmentUrl(doc)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    title="Descargar documento"
                  >
                    <FileTypeIcons type="document" width={24} height={24} />
                    <div className={classes.downloadOverlay}>
                      <GetApp fontSize="small" />
                    </div>
                  </a>
                ))}
                {actionPreview.videos?.map((video, index) => (
                  <a
                    key={`video-${index}`}
                    className={classes.attachmentItem}
                    href={getTriggerAttachmentUrl(video)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    title="Descargar video"
                  >
                    <FileTypeIcons type="video" width={24} height={24} />
                    <div className={classes.downloadOverlay}>
                      <GetApp fontSize="small" />
                    </div>
                  </a>
                ))}
                {actionPreview.images?.map((image, index) => (
                  <a
                    key={`img-${index}`}
                    className={classes.attachmentItem}
                    href={getTriggerAttachmentUrl(image)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    title="Descargar imagen"
                  >
                    <img
                      src={getTriggerAttachmentUrl(image)}
                      alt={`Attachment ${index + 1}`}
                    />
                    <div className={classes.downloadOverlay}>
                      <GetApp fontSize="small" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
      </div>
      <ConfirmModal
        open={modalConfirmOpen}
        message="¿Estás seguro que deseas eliminar este Trigger?"
        title="Eliminar Trigger"
        onConfirm={onDelete}
        onCancel={closeModalConfirm}
        onClose={closeModalConfirm}
      />
    </>
  );
}
